import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const NotificationSettings = () => {
  const [notificationSettings, setNotificationSettings] = useState({
    // Price Alerts
    priceAlerts: true,
    priceThreshold: 5,
    volumeAlerts: true,
    volumeThreshold: 50,
    
    // Trading Alerts
    orderFilled: true,
    orderCancelled: true,
    stopLossTriggered: true,
    takeProfitHit: true,
    marginCall: true,
    
    // Market Alerts
    marketNews: true,
    technicalSignals: true,
    algorithmAlerts: true,
    supportResistance: true,
    
    // Portfolio Alerts
    portfolioValue: true,
    portfolioThreshold: 10,
    profitLoss: true,
    plThreshold: 5,
    
    // Delivery Methods
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    webhookNotifications: false,
    
    // Frequency Settings
    realTimeAlerts: true,
    dailySummary: true,
    weeklySummary: true,
    monthlySummary: false,
    
    // Quiet Hours
    quietHours: true,
    quietStart: '22:00',
    quietEnd: '08:00',
    
    // Advanced Settings
    alertPriority: 'medium',
    maxAlertsPerHour: 10,
    duplicateFilter: true,
    alertSound: 'default'
  });

  const [customAlerts, setCustomAlerts] = useState([
    {
      id: 1,
      name: 'Bitcoin Above $50K',
      condition: 'BTC/USD > 50000',
      enabled: true,
      triggered: 0,
      created: '2024-01-15'
    },
    {
      id: 2,
      name: 'High Volume ETH',
      condition: 'ETH volume > 1M',
      enabled: true,
      triggered: 3,
      created: '2024-01-18'
    }
  ]);

  const [newAlert, setNewAlert] = useState({
    name: '',
    condition: '',
    symbol: 'BTC/USD',
    operator: '>',
    value: ''
  });

  const [showCreateAlert, setShowCreateAlert] = useState(false);

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'critical', label: 'Critical Only' }
  ];

  const soundOptions = [
    { value: 'default', label: 'Default' },
    { value: 'chime', label: 'Chime' },
    { value: 'bell', label: 'Bell' },
    { value: 'alert', label: 'Alert' },
    { value: 'none', label: 'No Sound' }
  ];

  const symbolOptions = [
    { value: 'BTC/USD', label: 'Bitcoin (BTC/USD)' },
    { value: 'ETH/USD', label: 'Ethereum (ETH/USD)' },
    { value: 'ADA/USD', label: 'Cardano (ADA/USD)' },
    { value: 'SOL/USD', label: 'Solana (SOL/USD)' },
    { value: 'DOT/USD', label: 'Polkadot (DOT/USD)' }
  ];

  const operatorOptions = [
    { value: '>', label: 'Greater than (>)' },
    { value: '<', label: 'Less than (<)' },
    { value: '>=', label: 'Greater than or equal (>=)' },
    { value: '<=', label: 'Less than or equal (<=)' },
    { value: '==', label: 'Equal to (==)' }
  ];

  const handleSettingChange = (setting, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleCheckboxChange = (setting, checked) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: checked
    }));
  };

  const handleCreateAlert = () => {
    if (newAlert?.name && newAlert?.value) {
      const alert = {
        id: customAlerts?.length + 1,
        name: newAlert?.name,
        condition: `${newAlert?.symbol} ${newAlert?.operator} ${newAlert?.value}`,
        enabled: true,
        triggered: 0,
        created: new Date()?.toISOString()?.split('T')?.[0]
      };
      setCustomAlerts(prev => [...prev, alert]);
      setNewAlert({ name: '', condition: '', symbol: 'BTC/USD', operator: '>', value: '' });
      setShowCreateAlert(false);
    }
  };

  const handleDeleteAlert = (alertId) => {
    setCustomAlerts(prev => prev?.filter(alert => alert?.id !== alertId));
  };

  const handleToggleAlert = (alertId) => {
    setCustomAlerts(prev => 
      prev?.map(alert => 
        alert?.id === alertId ? { ...alert, enabled: !alert?.enabled } : alert
      )
    );
  };

  const handleTestNotification = () => {
    // Simulate a test notification
    if (notificationSettings?.pushNotifications) {
      new Notification('CryptoWizard Pro Test', {
        body: 'This is a test notification from your trading platform.',
        icon: '/favicon.ico'
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Notification Settings</h3>
          <p className="text-sm text-muted-foreground">Configure alerts and notification preferences</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestNotification}
            iconName="Bell"
            iconPosition="left"
          >
            Test Notification
          </Button>
        </div>
      </div>
      {/* Delivery Methods */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Delivery Methods</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Notification Channels</label>
            <div className="space-y-2">
              <Checkbox
                label="Email Notifications"
                description="Receive alerts via email"
                checked={notificationSettings?.emailNotifications}
                onChange={(e) => handleCheckboxChange('emailNotifications', e?.target?.checked)}
              />
              <Checkbox
                label="Push Notifications"
                description="Browser push notifications"
                checked={notificationSettings?.pushNotifications}
                onChange={(e) => handleCheckboxChange('pushNotifications', e?.target?.checked)}
              />
              <Checkbox
                label="SMS Notifications"
                description="Text message alerts (premium feature)"
                checked={notificationSettings?.smsNotifications}
                onChange={(e) => handleCheckboxChange('smsNotifications', e?.target?.checked)}
              />
              <Checkbox
                label="Webhook Notifications"
                description="Send alerts to custom webhook URL"
                checked={notificationSettings?.webhookNotifications}
                onChange={(e) => handleCheckboxChange('webhookNotifications', e?.target?.checked)}
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <Select
              label="Alert Priority"
              description="Filter notifications by importance level"
              options={priorityOptions}
              value={notificationSettings?.alertPriority}
              onChange={(value) => handleSettingChange('alertPriority', value)}
            />
            
            <Select
              label="Alert Sound"
              description="Sound to play for notifications"
              options={soundOptions}
              value={notificationSettings?.alertSound}
              onChange={(value) => handleSettingChange('alertSound', value)}
            />
          </div>
        </div>
      </div>
      {/* Price & Volume Alerts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Price & Volume Alerts</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-3">
              <Checkbox
                label="Price Movement Alerts"
                description="Get notified of significant price changes"
                checked={notificationSettings?.priceAlerts}
                onChange={(e) => handleCheckboxChange('priceAlerts', e?.target?.checked)}
              />
              {notificationSettings?.priceAlerts && (
                <Input
                  label="Price Change Threshold (%)"
                  type="number"
                  value={notificationSettings?.priceThreshold}
                  onChange={(e) => handleSettingChange('priceThreshold', parseFloat(e?.target?.value))}
                  description="Alert when price changes by this percentage"
                />
              )}
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-3">
              <Checkbox
                label="Volume Spike Alerts"
                description="Get notified of unusual trading volume"
                checked={notificationSettings?.volumeAlerts}
                onChange={(e) => handleCheckboxChange('volumeAlerts', e?.target?.checked)}
              />
              {notificationSettings?.volumeAlerts && (
                <Input
                  label="Volume Increase Threshold (%)"
                  type="number"
                  value={notificationSettings?.volumeThreshold}
                  onChange={(e) => handleSettingChange('volumeThreshold', parseFloat(e?.target?.value))}
                  description="Alert when volume increases by this percentage"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Trading Alerts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Trading Alerts</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Checkbox
              label="Order Filled"
              description="When your buy/sell orders are executed"
              checked={notificationSettings?.orderFilled}
              onChange={(e) => handleCheckboxChange('orderFilled', e?.target?.checked)}
            />
            <Checkbox
              label="Order Cancelled"
              description="When your orders are cancelled"
              checked={notificationSettings?.orderCancelled}
              onChange={(e) => handleCheckboxChange('orderCancelled', e?.target?.checked)}
            />
            <Checkbox
              label="Stop Loss Triggered"
              description="When stop loss orders are executed"
              checked={notificationSettings?.stopLossTriggered}
              onChange={(e) => handleCheckboxChange('stopLossTriggered', e?.target?.checked)}
            />
          </div>
          
          <div className="space-y-2">
            <Checkbox
              label="Take Profit Hit"
              description="When take profit targets are reached"
              checked={notificationSettings?.takeProfitHit}
              onChange={(e) => handleCheckboxChange('takeProfitHit', e?.target?.checked)}
            />
            <Checkbox
              label="Margin Call"
              description="When margin requirements are not met"
              checked={notificationSettings?.marginCall}
              onChange={(e) => handleCheckboxChange('marginCall', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Market & Technical Alerts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Market & Technical Alerts</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Checkbox
              label="Market News"
              description="Important cryptocurrency news and updates"
              checked={notificationSettings?.marketNews}
              onChange={(e) => handleCheckboxChange('marketNews', e?.target?.checked)}
            />
            <Checkbox
              label="Technical Signals"
              description="Technical analysis signals and patterns"
              checked={notificationSettings?.technicalSignals}
              onChange={(e) => handleCheckboxChange('technicalSignals', e?.target?.checked)}
            />
          </div>
          
          <div className="space-y-2">
            <Checkbox
              label="Algorithm Alerts"
              description="PTS-WIZARD-V3 algorithm signals"
              checked={notificationSettings?.algorithmAlerts}
              onChange={(e) => handleCheckboxChange('algorithmAlerts', e?.target?.checked)}
            />
            <Checkbox
              label="Support/Resistance Levels"
              description="When price approaches key levels"
              checked={notificationSettings?.supportResistance}
              onChange={(e) => handleCheckboxChange('supportResistance', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Portfolio Alerts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Portfolio Alerts</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-3">
              <Checkbox
                label="Portfolio Value Changes"
                description="Alert on significant portfolio value changes"
                checked={notificationSettings?.portfolioValue}
                onChange={(e) => handleCheckboxChange('portfolioValue', e?.target?.checked)}
              />
              {notificationSettings?.portfolioValue && (
                <Input
                  label="Portfolio Change Threshold (%)"
                  type="number"
                  value={notificationSettings?.portfolioThreshold}
                  onChange={(e) => handleSettingChange('portfolioThreshold', parseFloat(e?.target?.value))}
                  description="Alert when portfolio value changes by this percentage"
                />
              )}
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-3">
              <Checkbox
                label="Profit/Loss Alerts"
                description="Alert on significant P&L changes"
                checked={notificationSettings?.profitLoss}
                onChange={(e) => handleCheckboxChange('profitLoss', e?.target?.checked)}
              />
              {notificationSettings?.profitLoss && (
                <Input
                  label="P&L Change Threshold (%)"
                  type="number"
                  value={notificationSettings?.plThreshold}
                  onChange={(e) => handleSettingChange('plThreshold', parseFloat(e?.target?.value))}
                  description="Alert when P&L changes by this percentage"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Custom Alerts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-md font-medium text-foreground">Custom Alerts</h4>
            <p className="text-sm text-muted-foreground">Create personalized alert conditions</p>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowCreateAlert(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Create Alert
          </Button>
        </div>

        {showCreateAlert && (
          <div className="bg-muted rounded-md p-4 mb-4">
            <h5 className="text-sm font-medium text-foreground mb-3">Create Custom Alert</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="Alert Name"
                placeholder="Enter alert name"
                value={newAlert?.name}
                onChange={(e) => setNewAlert(prev => ({ ...prev, name: e?.target?.value }))}
              />
              <Select
                label="Symbol"
                options={symbolOptions}
                value={newAlert?.symbol}
                onChange={(value) => setNewAlert(prev => ({ ...prev, symbol: value }))}
              />
              <Select
                label="Condition"
                options={operatorOptions}
                value={newAlert?.operator}
                onChange={(value) => setNewAlert(prev => ({ ...prev, operator: value }))}
              />
              <Input
                label="Value"
                type="number"
                placeholder="Enter threshold value"
                value={newAlert?.value}
                onChange={(e) => setNewAlert(prev => ({ ...prev, value: e?.target?.value }))}
              />
            </div>
            <div className="flex items-center space-x-2 mt-4">
              <Button variant="default" onClick={handleCreateAlert} size="sm">
                Create Alert
              </Button>
              <Button variant="outline" onClick={() => setShowCreateAlert(false)} size="sm">
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {customAlerts?.map((alert) => (
            <div key={alert?.id} className="flex items-center justify-between p-3 bg-muted rounded-md">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={alert?.enabled}
                  onChange={() => handleToggleAlert(alert?.id)}
                />
                <div>
                  <div className="text-sm font-medium text-foreground">{alert?.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {alert?.condition} • Triggered {alert?.triggered} times • Created {alert?.created}
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDeleteAlert(alert?.id)}
                iconName="Trash2"
              >
                Delete
              </Button>
            </div>
          ))}
        </div>
      </div>
      {/* Frequency & Timing */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Frequency & Timing</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Checkbox
                label="Real-time Alerts"
                description="Immediate notifications for critical events"
                checked={notificationSettings?.realTimeAlerts}
                onChange={(e) => handleCheckboxChange('realTimeAlerts', e?.target?.checked)}
              />
              <Checkbox
                label="Daily Summary"
                description="Daily portfolio and market summary"
                checked={notificationSettings?.dailySummary}
                onChange={(e) => handleCheckboxChange('dailySummary', e?.target?.checked)}
              />
              <Checkbox
                label="Weekly Summary"
                description="Weekly performance report"
                checked={notificationSettings?.weeklySummary}
                onChange={(e) => handleCheckboxChange('weeklySummary', e?.target?.checked)}
              />
              <Checkbox
                label="Monthly Summary"
                description="Monthly portfolio analysis"
                checked={notificationSettings?.monthlySummary}
                onChange={(e) => handleCheckboxChange('monthlySummary', e?.target?.checked)}
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-3">
              <Checkbox
                label="Quiet Hours"
                description="Disable non-critical alerts during specified hours"
                checked={notificationSettings?.quietHours}
                onChange={(e) => handleCheckboxChange('quietHours', e?.target?.checked)}
              />
              {notificationSettings?.quietHours && (
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="Start Time"
                    type="time"
                    value={notificationSettings?.quietStart}
                    onChange={(e) => handleSettingChange('quietStart', e?.target?.value)}
                  />
                  <Input
                    label="End Time"
                    type="time"
                    value={notificationSettings?.quietEnd}
                    onChange={(e) => handleSettingChange('quietEnd', e?.target?.value)}
                  />
                </div>
              )}
            </div>
            
            <Input
              label="Max Alerts Per Hour"
              type="number"
              value={notificationSettings?.maxAlertsPerHour}
              onChange={(e) => handleSettingChange('maxAlertsPerHour', parseInt(e?.target?.value))}
              description="Limit to prevent notification spam"
            />
            
            <Checkbox
              label="Duplicate Filter"
              description="Prevent duplicate alerts for the same condition"
              checked={notificationSettings?.duplicateFilter}
              onChange={(e) => handleCheckboxChange('duplicateFilter', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Save Actions */}
      <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border">
        <Button variant="outline">
          Reset to Defaults
        </Button>
        <Button variant="default" iconName="Save" iconPosition="left">
          Save Notification Settings
        </Button>
      </div>
    </div>
  );
};

export default NotificationSettings;